/**
 * The only module that talks to the backend.
 *
 *   const { courses } = await api.get('/courses', { query: { category: 'Testing' } });
 *   await api.post('/billing/checkout', { body: { course_id: 1 } });
 *
 * - Adds the bearer token, and a request id the server echoes into its logs.
 * - On an expired access token it refreshes ONCE (single flight: parallel requests share one refresh)
 *   and replays the request. If the server refuses the refresh the session is ended; if it is merely busy
 *   or unreachable the session is kept and the real problem is reported.
 * - Every failure becomes an ApiError with a readable message; network failures have status 0.
 */
import { bus } from './bus.js';
import { config } from './config.js';
import { ApiError } from './errors.js';
import { tokens } from './tokens.js';

const RETRYABLE_AUTH_CODES = new Set(['token_expired', 'invalid_token', 'unauthorized']);
let refreshing = null;

function requestId() {
  return globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : String(Date.now()) + Math.random().toString(16).slice(2);
}

export function buildUrl(path, query) {
  const url = config.apiBase + (path.startsWith('/') ? path : `/${path}`);
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function parse(response) {
  const type = response.headers.get('content-type') || '';
  if (type.includes('application/json')) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }
  return null;
}

function toError(response, body) {
  const err = body && body.error;
  if (err && typeof err === 'object') return new ApiError(response.status, err.code || 'error', err.message || 'Request failed.', err.details ?? null);
  return new ApiError(response.status, 'http_error', response.statusText || 'Request failed.');
}

/** One refresh at a time across every open tab: the refresh token is single use, so two tabs must take turns. */
const exclusive = (fn) => (globalThis.navigator?.locks ? navigator.locks.request('vconnect-refresh', fn) : fn());
const pause = (ms) => new Promise((done) => setTimeout(done, ms));

async function refreshOnce() {
  const refresh = tokens.refresh; // read inside the lock: another tab may have rotated it while we waited
  if (!refresh) throw new ApiError(401, 'unauthorized', 'Log in to continue.');
  const response = await fetch(buildUrl('/auth/refresh'), {
    method: 'POST',
    headers: { Authorization: `Bearer ${refresh}`, Accept: 'application/json' },
  });
  const body = await parse(response);
  if (!response.ok) throw toError(response, body);
  tokens.set(body);
}

async function refreshTokens() {
  if (!refreshing) {
    refreshing = exclusive(async () => {
      try {
        await refreshOnce();
      } catch (error) {
        if (error.code !== 'token_rotated') throw error;
        await pause(250); // a tab without locks renewed it a moment ago; its new token is in storage by now
        await refreshOnce();
      }
    }).catch((error) => {
      // A network failure is not a refusal. Only the server saying "no" ends the session.
      throw error instanceof ApiError ? error : new ApiError(0, 'network', 'We could not reach the server. Check your connection and try again.');
    }).finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
}

async function send(method, path, { query, body, auth = true, signal, timeout = config.requestTimeoutMs, raw = false, form } = {}) {
  const headers = { Accept: raw ? '*/*' : 'application/json', 'X-Request-ID': requestId() };
  const init = { method, headers };
  if (form) init.body = form;
  else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  if (auth && tokens.access) headers.Authorization = `Bearer ${tokens.access}`;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new DOMException('timeout', 'TimeoutError')), timeout);
  const onAbort = () => controller.abort(signal.reason);
  if (signal) signal.aborted ? controller.abort(signal.reason) : signal.addEventListener('abort', onAbort, { once: true });
  init.signal = controller.signal;

  let response;
  try {
    response = await fetch(buildUrl(path, query), init);
  } catch (error) {
    if (signal?.aborted) throw error; // the caller cancelled; not a failure
    throw new ApiError(0, error?.name === 'TimeoutError' ? 'timeout' : 'network', 'We could not reach the server. Check your connection and try again.');
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onAbort);
  }
  return response;
}

async function request(method, path, options = {}, attempt = 0) {
  const response = await send(method, path, options);
  const raw = options.raw;
  const body = raw && response.ok ? null : await parse(response);
  if (response.ok) return raw ? response : body;

  const error = toError(response, body);
  const canRetry = options.auth !== false && attempt === 0 && response.status === 401 && RETRYABLE_AUTH_CODES.has(error.code) && tokens.refresh;
  if (canRetry) {
    try {
      await refreshTokens();
    } catch (refreshError) {
      if (refreshError.status === 401 || refreshError.status === 403) {
        tokens.clear();
        bus.emit('session:expired', refreshError);
        throw error;
      }
      throw refreshError; // busy server or no network: keep the session, tell the person what happened
    }
    return request(method, path, options, 1);
  }
  if (response.status === 401 && options.auth !== false) {
    tokens.clear();
    bus.emit('session:expired', error);
  }
  throw error;
}

export const api = {
  get: (path, options) => request('GET', path, options),
  post: (path, options) => request('POST', path, options),
  put: (path, options) => request('PUT', path, options),
  patch: (path, options) => request('PATCH', path, options),
  delete: (path, options) => request('DELETE', path, options),
  /** multipart upload: api.upload('/files', formData) */
  upload: (path, form, options = {}) => request('POST', path, { ...options, form, timeout: options.timeout ?? 120000 }),
  /** Authenticated download as a Blob (CSV exports, printable pages). */
  async blob(path, options = {}) {
    const response = await request('GET', path, { ...options, raw: true });
    return { blob: await response.blob(), type: response.headers.get('content-type') || '' };
  },
  refresh: refreshTokens,
};

/**
 * Open an authenticated HTML page (invoice, certificate) in a new tab. The tab is opened synchronously
 * inside the click, because browsers block pop-ups that open after an await.
 */
export async function openPrintable(path) {
  const tab = window.open('', '_blank');
  try {
    const { blob } = await api.blob(path);
    const url = URL.createObjectURL(new Blob([blob], { type: 'text/html' }));
    if (tab) tab.location.href = url;
    else window.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (error) {
    tab?.close();
    throw error;
  }
}

/** Save an authenticated download (CSV) to the user's disk. */
export async function saveDownload(path, filename, options) {
  const { blob } = await api.blob(path, options);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
